import { useState } from 'react';
import {
  Button,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Select,
  Spinner,
  Stack,
  Badge,
  Text,
  Textarea,
  VStack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Plus, Trash2 } from 'lucide-react';
import { ContentGenerationLayout } from '@duke-hq/ui/components/ContentGeneration/ContentGenerationLayout';
import { ContentListItem } from '@duke-hq/ui/components/ContentGeneration/ContentListItem';
import {
  editAction,
  deleteAction,
} from '@duke-hq/ui/components/ContentGeneration/contentListItemActions';
import { ListCard } from '@duke-hq/ui/components/Cards/ListCard';
import { ConfirmationDialog } from '@duke-hq/ui/components/DialogBox/ConfirmationDialog';
import { withPermission } from '../withPermission';
import { trpcAdmin, type RouterOutputs } from '../lib/trpcAdmin';
import { testIds } from './testIds';

type ContentProfile = RouterOutputs['contentProfiles']['all'][number];
type ContentRole = RouterOutputs['contentRoles']['all'][number];
type ContentPersona = RouterOutputs['contentPersonas']['all'][number];

interface CastMemberFormData {
  contentRoleId: string;
  contentPersonaId: string;
}

type CastMemberState = CastMemberFormData & { id: string };

interface ProfileFormData {
  title: string;
  acsfLevel: number;
  description: string;
  learnerContext: string;
  castMembers: CastMemberFormData[];
}

const ACSF_LEVELS = [1, 2, 3, 4, 5] as const;

export const ContentProfilesPage = withPermission('canManageCourseContent', () => {
  const toast = useToast();
  const drawer = useDisclosure();
  const deleteDialog = useDisclosure();

  const [selectedProfile, setSelectedProfile] = useState<ContentProfile | null>(null);
  const [profileToDelete, setProfileToDelete] = useState<ContentProfile | null>(null);

  const profilesQuery = trpcAdmin.contentProfiles.all.useQuery();
  const rolesQuery = trpcAdmin.contentRoles.all.useQuery();
  const personasQuery = trpcAdmin.contentPersonas.all.useQuery();

  const createMutation = trpcAdmin.contentProfiles.create.useMutation({
    onSuccess: () => {
      toast({ title: t`Profile created`, status: 'success', duration: 3000 });
      void profilesQuery.refetch();
      handleCloseDrawer();
    },
    onError: (error) => {
      toast({
        title: t`Failed to create profile`,
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    },
  });

  const updateMutation = trpcAdmin.contentProfiles.update.useMutation({
    onSuccess: () => {
      toast({ title: t`Profile updated`, status: 'success', duration: 3000 });
      void profilesQuery.refetch();
      handleCloseDrawer();
    },
    onError: (error) => {
      toast({
        title: t`Failed to update profile`,
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    },
  });

  const archiveMutation = trpcAdmin.contentProfiles.archive.useMutation({
    onSuccess: () => {
      toast({ title: t`Profile deleted`, status: 'success', duration: 3000 });
      void profilesQuery.refetch();
      deleteDialog.onClose();
      setProfileToDelete(null);
    },
    onError: (error) => {
      toast({
        title: t`Failed to delete profile`,
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    },
  });

  function handleCloseDrawer() {
    drawer.onClose();
    setSelectedProfile(null);
  }

  function handleOpenCreate() {
    setSelectedProfile(null);
    drawer.onOpen();
  }

  function handleOpenEdit(profile: ContentProfile) {
    setSelectedProfile(profile);
    drawer.onOpen();
  }

  function handleOpenDelete(profile: ContentProfile) {
    setProfileToDelete(profile);
    deleteDialog.onOpen();
  }

  function handleConfirmDelete() {
    if (profileToDelete) {
      archiveMutation.mutate(profileToDelete.id);
    }
  }

  function handleSubmit(data: ProfileFormData) {
    if (selectedProfile) {
      updateMutation.mutate({ id: selectedProfile.id, ...data });
    } else {
      createMutation.mutate(data);
    }
  }

  const profiles = profilesQuery.data ?? [];
  const roles = rolesQuery.data ?? [];
  const personas = personasQuery.data ?? [];
  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <>
      <ContentGenerationLayout
        breadcrumb={<Trans>Unit Generation</Trans>}
        title={<Trans>Content Profiles</Trans>}
        actions={
          <Button
            leftIcon={<Plus size={16} />}
            colorScheme="brand"
            size="sm"
            onClick={handleOpenCreate}
            data-testid={testIds.addButton}
          >
            <Trans>Add new profile</Trans>
          </Button>
        }
        drawer={
          drawer.isOpen
            ? {
                isOpen: true,
                onClose: handleCloseDrawer,
                title: <Trans>Profile Settings</Trans>,
                body: (
                  <ProfileForm
                    profile={selectedProfile}
                    roles={roles}
                    personas={personas}
                    onSubmit={handleSubmit}
                    isSaving={isSaving}
                  />
                ),
                size: 'lg',
              }
            : undefined
        }
      >
        {profilesQuery.isPending ? (
          <Spinner data-testid={testIds.loadingSpinner} />
        ) : profiles.length === 0 ? (
          <Text color="fg.muted" data-testid={testIds.emptyState}>
            <Trans>No content profiles yet. Add a new profile to get started.</Trans>
          </Text>
        ) : (
          <ListCard
            cardTitle={<Trans>Profiles</Trans>}
            cardSubtitle={
              <Trans>Presets for the generation of content tailored to a specific audience</Trans>
            }
            cardContent={
              <VStack spacing="2" data-testid={testIds.profilesList}>
                {profiles.map((profile) => (
                  <ContentListItem
                    key={profile.id}
                    title={profile.title}
                    description={profile.description}
                    tags={
                      <Badge colorScheme="purple" variant="subtle" textTransform="none">
                        {t`ACSF Level ${profile.acsfLevel}`}
                      </Badge>
                    }
                    onClick={() => handleOpenEdit(profile)}
                    menuActions={[
                      editAction(() => handleOpenEdit(profile)),
                      deleteAction(() => handleOpenDelete(profile)),
                    ]}
                  />
                ))}
              </VStack>
            }
          />
        )}
      </ContentGenerationLayout>

      <ConfirmationDialog
        isOpen={deleteDialog.isOpen}
        onClose={deleteDialog.onClose}
        title={<Trans>Delete Profile</Trans>}
        body={
          <Text data-testid={testIds.deleteDialog}>
            <Trans>
              Are you sure you want to delete &quot;{profileToDelete?.title}&quot;? This action
              cannot be undone.
            </Trans>
          </Text>
        }
        onConfirm={handleConfirmDelete}
        confirmLabel={<Trans>Delete</Trans>}
        variant="destructive"
        isLoading={archiveMutation.isPending}
      />
    </>
  );
});

// -- Profile form -----------------------------------------------------

interface ProfileFormProps {
  profile: ContentProfile | null;
  roles: ContentRole[];
  personas: ContentPersona[];
  onSubmit: (data: ProfileFormData) => void;
  isSaving: boolean;
}

function ProfileForm({ profile, roles, personas, onSubmit, isSaving }: ProfileFormProps) {
  const [title, setTitle] = useState(profile?.title ?? '');
  const [acsfLevel, setAcsfLevel] = useState<number>(profile?.acsfLevel ?? 1);
  const [description, setDescription] = useState(profile?.description ?? '');
  const [learnerContext, setLearnerContext] = useState(profile?.learnerContext ?? '');
  const [castMembers, setCastMembers] = useState<CastMemberState[]>(
    profile?.castMembers.map((m) => ({
      id: crypto.randomUUID(),
      contentRoleId: m.contentRoleId,
      contentPersonaId: m.contentPersonaId,
    })) ?? [{ id: crypto.randomUUID(), contentRoleId: '', contentPersonaId: '' }],
  );

  const isValid =
    title.trim().length > 0 &&
    description.trim().length > 0 &&
    learnerContext.trim().length > 0 &&
    castMembers.length > 0 &&
    castMembers.every((m) => m.contentRoleId !== '' && m.contentPersonaId !== '');

  const hasIncompleteCastMember = castMembers.some(
    (m) => m.contentRoleId === '' || m.contentPersonaId === '',
  );

  function handleAddCastMember() {
    setCastMembers([
      ...castMembers,
      { id: crypto.randomUUID(), contentRoleId: '', contentPersonaId: '' },
    ]);
  }

  function handleRemoveCastMember(index: number) {
    setCastMembers(castMembers.filter((_, i) => i !== index));
  }

  function handleCastMemberChange(index: number, field: keyof CastMemberFormData, value: string) {
    setCastMembers(castMembers.map((m, i) => (i === index ? { ...m, [field]: value } : m)));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (isValid) {
      onSubmit({
        title: title.trim(),
        acsfLevel,
        description: description.trim(),
        learnerContext: learnerContext.trim(),
        castMembers: castMembers.map(({ id: _, ...rest }) => rest),
      });
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <Stack spacing="5">
        <FormControl isRequired>
          <FormLabel>
            <Trans>Title</Trans>
          </FormLabel>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t`e.g. Foundation Workplace Skills`}
            maxLength={255}
            data-testid={testIds.titleInput}
          />
        </FormControl>

        <FormControl isRequired>
          <FormLabel>
            <Trans>ACSF Level</Trans>
          </FormLabel>
          <Select
            value={acsfLevel}
            onChange={(e) => setAcsfLevel(Number(e.target.value))}
            data-testid={testIds.acsfLevelSelect}
          >
            {ACSF_LEVELS.map((level) => (
              <option key={level} value={level}>
                {t`Level ${level}`}
              </option>
            ))}
          </Select>
        </FormControl>

        <FormControl isRequired>
          <FormLabel>
            <Trans>Description</Trans>
          </FormLabel>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t`Short summary of this profile...`}
            maxLength={1000}
            rows={3}
            data-testid={testIds.descriptionInput}
          />
        </FormControl>

        <FormControl isRequired>
          <FormLabel>
            <Trans>Learner Context</Trans>
          </FormLabel>
          <Textarea
            value={learnerContext}
            onChange={(e) => setLearnerContext(e.target.value)}
            placeholder={t`Describe the target audience for this profile...`}
            maxLength={5000}
            rows={4}
            data-testid={testIds.learnerContextInput}
          />
        </FormControl>

        <Stack spacing="3">
          <HStack justify="space-between">
            <FormLabel mb="0">
              <Trans>Cast Members</Trans>
            </FormLabel>
            <Button
              size="xs"
              variant="ghost"
              leftIcon={<Plus size={14} />}
              onClick={handleAddCastMember}
              isDisabled={hasIncompleteCastMember}
              data-testid={testIds.addCastMemberButton}
            >
              <Trans>Add Cast Member</Trans>
            </Button>
          </HStack>

          {castMembers.map((member, index) => (
            <HStack
              key={member.id}
              p="3"
              borderWidth="1px"
              borderColor="border.default"
              borderRadius="md"
              spacing="3"
              align="end"
              data-testid={testIds.castMemberCard}
            >
              <FormControl isRequired flex="1">
                <FormLabel fontSize="sm">
                  <Trans>Role</Trans>
                </FormLabel>
                <Select
                  size="sm"
                  value={member.contentRoleId}
                  onChange={(e) => handleCastMemberChange(index, 'contentRoleId', e.target.value)}
                  placeholder={t`Select role`}
                  data-testid={testIds.castMemberRoleSelect}
                >
                  {roles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.title}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl isRequired flex="1">
                <FormLabel fontSize="sm">
                  <Trans>Persona</Trans>
                </FormLabel>
                <Select
                  size="sm"
                  value={member.contentPersonaId}
                  onChange={(e) =>
                    handleCastMemberChange(index, 'contentPersonaId', e.target.value)
                  }
                  placeholder={t`Select persona`}
                  data-testid={testIds.castMemberPersonaSelect}
                >
                  {personas.map((persona) => (
                    <option key={persona.id} value={persona.id}>
                      {persona.name}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <IconButton
                aria-label={t`Remove cast member`}
                icon={<Trash2 size={14} />}
                size="sm"
                variant="ghost"
                colorScheme="red"
                isDisabled={castMembers.length <= 1}
                onClick={() => handleRemoveCastMember(index)}
                data-testid={testIds.castMemberDeleteButton}
              />
            </HStack>
          ))}
        </Stack>

        <Button
          type="submit"
          colorScheme="brand"
          isLoading={isSaving}
          isDisabled={!isValid}
          data-testid={testIds.saveButton}
        >
          {profile ? <Trans>Update</Trans> : <Trans>Save</Trans>}
        </Button>
      </Stack>
    </form>
  );
}
